import _ from "lodash";
import React, { useContext } from "react";
import CriteriaRubricAssessment from "AppComponents/CriteriaRubricAssessment";
import SupervisorComment from "AppComponents/SupervisorComment";
import classes from "./CriteriaRubricAssessmentWithComments.scss";
import {
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
} from "Projects/Constants/stringConstants";
import { useI18n } from "Hooks";
import { ProjectPlannerContext } from "Projects/modules/ProjectModules";
import ACLStore from "lib/aclStore";

const styles = {
  assessmentContainerStyle: {
    marginBottom: "32px",
  },
  titleComponentContainerStyle: {
    padding: "16px 0",
  },
};

const CriteriaRubricAssessmentWithComments = props => {
  const {
    label,
    subText,
    assessmentRemarks,
    ibPYPElementRatings,
    isRatingEditable,
    showSupervisorComment,
    typeOfScale,
    criteria,
    commentLabelLocale,
    onSelectMark,
    updateRemarks,
    assessmentContainerStyle,
    assessmentHeading,
    projectGroupType,
    finalCriteria,
    isGradeUnderComputation,
    showMarksBox = true,
    ibPYPElementRemarks,
    onUpdateCriteriaComment,
    titleComponentContainerStyle,
    isFinalGradeDynamic = false,
    finalGradeElementType,
    showEmptyValueInSelection,
    assessmentLeafNodLabelStyles,
    assessmentInformationIconStyles,
    flatListedAssessmentTool,
    isDescriptorV2,
  } = props;

  const showUpdatedCommentUI = ACLStore.can(
    "FeatureFlag:EnableStrandCommenting"
  );

  const { t } = useI18n(["common"]);
  const { projectId } = useContext(ProjectPlannerContext);
  const ratedCriteria = _.map(ibPYPElementRatings, item => {
    return {
      ...item,
      ibPYPElementId: item?.ibPYPElementId,
      value: item?.value,
      criteriaType: item?.academicCriteriaSet?.criteriaType,
    };
  });

  const getHeadingLabel = ({ projectGroupType }) => {
    switch (projectGroupType) {
      case PROJECT_GROUP_TYPE_DP_EE:
      case PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT:
      case PROJECT_GROUP_TYPE_DP_TOK_ESSAY:
      case PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION:
        return t("project:supervisor_s_comment");
      default:
        return t(commentLabelLocale);
    }
  };

  const getSubheadingLabel = ({ projectGroupType }) => {
    switch (projectGroupType) {
      case PROJECT_GROUP_TYPE_DP_EE:
        return isDescriptorV2
          ? ""
          : t(
              "project:this_comment_is_to_justify_the_evaluation_above_and_will_not_be_added_to_the_label",
              {
                label: t("project:rppf"),
              }
            );
      case PROJECT_GROUP_TYPE_DP_TOK_ESSAY:
        return t(
          "project:this_comment_is_to_justify_the_evaluation_above_and_will_not_be_added_to_the_label",
          {
            label: t("project:tkppf"),
          }
        );
      default:
        return "";
    }
  };

  const getCriteriaData = () => {
    if (isDescriptorV2) {
      _.forEach(criteria, eachCriteria => {
        const matchingDescriptors = _.filter(
          flatListedAssessmentTool.descriptors,
          d => d.objective.parent === eachCriteria.id
        );
        const groupedByLevel = _.groupBy(
          matchingDescriptors,
          d => d.achievementLevel.id
        );

        eachCriteria.achievementLevel = _.map(
          groupedByLevel,
          descriptorsGroup => {
            const { achievementLevel } = _.first(descriptorsGroup) || {};
            // Collect all descriptors (e.g. the objective object or other descriptor fields)
            const descriptors = _.uniqBy(descriptorsGroup, "id");
            return {
              ...achievementLevel,
              descriptors,
            };
          }
        );
      });
    }
    const criteriaArray = [
      {
        criteria: criteria,
        headerLabel: label,
        headerSubText: subText,
        helpUID: "criteriaRubricAssessment",
        hasMarksBox: showMarksBox,
        ratingType: "NUMBERS",
        showCommentForRating:
          showUpdatedCommentUI &&
          !_.includes(
            [
              PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
              PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
            ],
            projectGroupType
          ),
        showModalUIForDescriptors: showUpdatedCommentUI,
      },
    ];
    if (finalCriteria) {
      criteriaArray.push({
        criteria: [
          {
            ...finalCriteria,
            isFinalGradeDynamic,
            ...(isFinalGradeDynamic ? { type: finalGradeElementType } : {}),
          },
        ],
        headerLabel: showUpdatedCommentUI ? "" : t("project:predicted_grade"),
        ratingType: "ALPHABET",
        showGradeComputationLoader: true,
        showCommentForRating: false,
        showModalUIForDescriptors: showUpdatedCommentUI,
        showInfoIcon: !_.includes(
          [
            PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
            PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
          ],
          projectGroupType
        ),
      });
    }
    return criteriaArray;
  };

  return (
    <div className={classes.container} style={assessmentContainerStyle}>
      <div
        className={classes.assessmentContainer}
        style={!showUpdatedCommentUI ? styles.assessmentContainerStyle : {}}
      >
        <CriteriaRubricAssessment
          typeOfScale={typeOfScale}
          ratedCriteria={ratedCriteria}
          onSelectMark={onSelectMark}
          isEditable={isRatingEditable}
          assessmentHeading={assessmentHeading}
          criteriaLabel={t("common:total")}
          projectGroupType={projectGroupType}
          criteriaData={getCriteriaData()}
          isGradeUnderComputation={isGradeUnderComputation}
          ibPYPElementRemarks={ibPYPElementRemarks}
          onUpdateCriteriaComment={onUpdateCriteriaComment}
          titleComponentContainerStyle={
            titleComponentContainerStyle ?? styles.titleComponentContainerStyle
          }
          projectId={projectId}
          showEmptyValueInSelection={showEmptyValueInSelection}
          assessmentInformationIconStyles={assessmentInformationIconStyles}
          assessmentLeafNodLabelStyles={assessmentLeafNodLabelStyles}
          shouldShowDescriptor
          flatListedAssessmentTool={flatListedAssessmentTool}
          isDescriptorV2={isDescriptorV2}
        />
      </div>
      {showSupervisorComment && (
        <div>
          <SupervisorComment
            key={projectId}
            assessmentRemarks={assessmentRemarks}
            headingLabel={getHeadingLabel({ projectGroupType })}
            subheadingLabel={getSubheadingLabel({ projectGroupType })}
            updateRemarks={updateRemarks}
            disabled={!isRatingEditable}
          />
        </div>
      )}
    </div>
  );
};

CriteriaRubricAssessmentWithComments.defaultProps = {
  criteria: [],
  label: "",
  subText: "",
  ibPYPElementRatings: [],
  isRatingEditable: true,
  showSupervisorComment: true,
  typeOfScale: "evaluationScale",
  commentLabelLocale: "common:comment_plural",
  assessmentHeading: "",
};

export default CriteriaRubricAssessmentWithComments;
